/**
 * TOML Format Tool
 *
 * Format/prettify TOML by parsing and re-stringifying.
 */

import { parse as parseToml, stringify as stringifyToml } from 'smol-toml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const TomlFormatTool: ITransformerTool = {
  id: 'toml-format',
  name: 'TOML Format',
  description: 'Format and prettify TOML by normalizing structure',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['toml', 'format', 'beautify', 'prettify', 'indent'],

  seo: {
    slug: 'toml-formatter',
    pageTitle: 'TOML Formatter - Free Online Tool | SWE DevTools',
    metaDescription: 'Format and prettify TOML configuration files. Normalize structure with clean indentation. Fast, private, runs entirely in your browser.',
    keywords: ['toml formatter', 'toml beautifier', 'format toml online', 'toml prettify', 'toml indent'],
    relatedTools: ['toml-minify', 'toml-validate', 'toml-to-json'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return '';

    try {
      let parsed: unknown = parseToml(text);

      if (config.sortKeys) {
        parsed = sortObjectKeys(parsed);
      }

      return stringifyToml(parsed as Record<string, unknown>).trimEnd();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to format TOML';
      throw ToolError.execution(message);
    }
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
